/**
 * Retailer links, in one place.
 *
 * The rule this file enforces: a Buy link must go to the actual book. Not a
 * search results page, not an author's Amazon store, not a retailer's home
 * page. If we do not have a real link to the book on a retailer, that retailer
 * is not offered — a Buy button that lands the reader on a search box is worse
 * than no Buy button.
 *
 * There is no way to build one of these from scratch: `books` does have
 * `isbn` and `isbn13` columns, but this module never reads them, and an ISBN
 * alone doesn't give a genuine product URL for most retailers. `purchase_links`
 * is the only source of one. Nothing here invents a link.
 */

export const AFFILIATE = {
  amazonUS: 'chisomebieleh-20',
  amazonCA: 'ehssburiedinbooks',
  bookshop: '121575',
};

function addParam(url, key, value) {
  return url + (url.includes('?') ? '&' : '?') + `${key}=${value}`;
}

function hostOf(url) {
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h.startsWith('www.') ? h.slice(4) : h;
  } catch {
    return null;
  }
}

function pathOf(url) {
  try { return new URL(url).pathname; } catch { return ''; }
}

/**
 * Paths that identify a single book on each retailer we can recognise.
 *
 * Amazon is the awkward one: /dp/, /gp/product/ and /gp/aw/d/ are all book
 * pages, while /author/, /stores/ and /product-reviews/ are not, and all six
 * get saved under the "Amazon" retailer name.
 */
const AMAZON_BOOK  = [/\/dp\/[A-Z0-9]/i, /\/gp\/product\/[A-Z0-9]/i, /\/gp\/aw\/d\/[A-Z0-9]/i];
const AMAZON_NOT   = [/\/author\//i, /\/stores\//i, /\/product-reviews\//i, /^\/s$/i];

const BOOK_PATHS = {
  'amazon.com':         AMAZON_BOOK,
  'amazon.ca':          AMAZON_BOOK,
  'amazon.co.uk':       AMAZON_BOOK,
  'amazon.com.au':      AMAZON_BOOK,
  'amazon.de':          AMAZON_BOOK,
  'barnesandnoble.com': [/^\/w\//i],
  'bookshop.org':       [/^\/p\//i, /^\/a\/\d+\/\d+/, /^\/books\//i],
  'kobo.com':           [/\/ebook\//i, /\/audiobook\//i],
  'books.apple.com':    [/\/book\//i, /\/audiobook\//i],
  'booksamillion.com':  [/^\/p\//i],
  'indigo.ca':          [/\/products?\//i, /\/[a-z0-9-]+\/[a-z0-9-]+\/[0-9a-f-]{8,}\.html/i,
                         /\/[a-z0-9-]+\/[a-z0-9-]+\/\d{10,13}\.html/i],
};

/**
 * Is this link worth showing as a Buy option?
 *
 * Deliberately conservative: it rejects only what it can positively identify
 * as not-a-book-page. A host we do not have rules for is kept, because a
 * universal book link (books2read.com/b/…, mybook.to/…) and an author selling
 * from their own site are both real ways to buy the book, and guessing wrong
 * there would delete links authors chose to add.
 *
 * "Author Website" is always kept — authors asked for that field and it is
 * theirs to point wherever they sell.
 */
export function isBookLink(retailer, url) {
  if (!url || typeof url !== 'string') return false;
  if (retailer === 'Author Website') return true;

  const host = hostOf(url);
  if (!host) return false;

  const patterns = BOOK_PATHS[host];
  if (!patterns) return true;               // unknown host — cannot prove it is bad

  const path = pathOf(url);
  if (host.startsWith('amazon.') && AMAZON_NOT.some(re => re.test(path))) return false;
  return patterns.some(re => re.test(path));
}

/**
 * The links a reader gets Buy buttons for, from the two places a book keeps them.
 *
 * `fromTable` is the purchase_links table, `fromBook` is the books.purchase_links
 * column. The same address can be saved in both, and the app offers it once: the
 * FIRST copy wins (the table's, then the column's), and only that copy is judged by
 * isBookLink. So an address saved as an Amazon author page in the table and as
 * 'Author Website' in the column is dropped, and the reader sees no button.
 *
 * getPurchaseLinks calls this. The SQL copy (buy_link_tally(), ticket 48) counts
 * books with nowhere to buy the same way, and check:book-link-parity runs this on
 * scripts/book-link-book-cases.json. Change the order, the key or the filter here
 * and change the SQL in the same commit.
 */
export function visibleBuyLinks(fromTable, fromBook) {
  // Key on the URL so it is only offered once, whichever store it came from.
  const seen = new Set();
  const merged = [];
  for (const link of [...fromTable, ...fromBook]) {
    if (!link || !link.url) continue;
    const key = String(link.url).trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(link);
  }
  return merged.filter(link => isBookLink(link.retailer, link.url));
}

/**
 * Tag a book link for affiliate credit. Which Amazon tag applies is decided by
 * the store the link points at — amazon.ca gets the CA tag, amazon.com the US
 * one.
 *
 * This is NOT reader geo-routing. The PRD wants the tag chosen by the reader's
 * country (profile first, IP as fallback), which needs a country field on the
 * reader profile that does not exist yet. Until it does, the store in the URL
 * is the only signal available.
 *
 * Bookshop only gets rewritten when an ISBN can be read out of the URL, since
 * /a/{id}/{isbn} is a real page for that book. Without one the link is left
 * exactly as it came in: an untagged link to the right book beats a tagged
 * link to a shop front the reader then has to search.
 */
export function withAffiliate(url) {
  if (!url) return url;
  if (url.includes('amazon.ca'))  return addParam(url, 'tag', AFFILIATE.amazonCA);
  if (url.includes('amazon.com')) return addParam(url, 'tag', AFFILIATE.amazonUS);
  if (url.includes('bookshop.org')) {
    const isbn = isbnFrom(url);
    return isbn ? `https://bookshop.org/a/${AFFILIATE.bookshop}/${isbn}` : url;
  }
  return url;
}

/**
 * Pull a real ISBN out of a URL, or null.
 *
 * Reads the ean/isbn query parameter first. Scanning the whole URL for digits
 * is not safe here: a Bookshop product path ends in a hex slug, and
 * .../the-silence-of-deceit-jillian-eagan/33d9173917750612?ean=9798999420107
 * yields "9173917750612" from the slug — thirteen digits, not an ISBN, and
 * rewriting to it produced a dead link to a book that does not exist.
 *
 * Whatever the source, the value still has to look like an ISBN: 13 digits
 * beginning 978 or 979, or 10 digits with an optional X check character.
 */
function isbnFrom(url) {
  const valid = s => /^97[89]\d{10}$/.test(s) || /^\d{9}[\dX]$/i.test(s);
  let param = null;
  try {
    const qs = new URL(url).searchParams;
    param = qs.get('ean') || qs.get('isbn') || qs.get('EAN');
  } catch { /* not parseable — fall through to the scan */ }
  if (param) {
    const cleaned = param.replace(/[^0-9Xx]/g, '');
    if (valid(cleaned)) return cleaned;
  }
  for (const m of url.matchAll(/\d{9}[\dX]|\d{13}/gi)) {
    if (valid(m[0])) return m[0];
  }
  return null;
}
